import type {
  LoginOptionsResponse
} from "@camp-points/shared";
import {
  useEffect,
  useState
} from "react";
import {
  signInWithCustomToken
} from "firebase/auth";
import { BrandShell } from "../components/BrandShell";
import {
  getLoginOptions,
  loginWithPin
} from "../lib/api";
import { auth } from "../lib/firebase";

export function LoginPage({
  slug,
  onAuthenticated
}: {
  slug: string;
  onAuthenticated: (
    organizationId: string
  ) => void | Promise<void>;
}) {
  const [
    options,
    setOptions
  ] =
    useState<LoginOptionsResponse | null>(
      null
    );

  const [
    membershipId,
    setMembershipId
  ] = useState("");

  const [pin, setPin] =
    useState("");

  const [error, setError] =
    useState("");

  const [submitting, setSubmitting] =
    useState(false);

  useEffect(() => {
    getLoginOptions(slug)
      .then((result) => {
        setOptions(result);
        setMembershipId(
          result.staff[0]
            ?.membershipId ?? ""
        );
      })
      .catch((caught: unknown) => {
        setError(
          caught instanceof Error
            ? caught.message
            : "The camp could not be loaded."
        );
      });
  }, [slug]);

  async function submit(
    event: React.FormEvent
  ) {
    event.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const result =
        await loginWithPin({
          slug,
          membershipId,
          pin
        });

      await signInWithCustomToken(
        auth,
        result.customToken
      );

      await onAuthenticated(
        result.organizationId
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Sign-in failed."
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!options) {
    return (
      <BrandShell eyebrow="Staff sign-in">
        <p>
          {error || "Loading staff…"}
        </p>
      </BrandShell>
    );
  }

  return (
    <BrandShell
      branding={options.branding}
      eyebrow="Staff sign-in"
    >
      <form onSubmit={submit}>
        <label>
          Staff member
          <select
            value={membershipId}
            onChange={(event) =>
              setMembershipId(
                event.target.value
              )
            }
          >
            {options.staff.map(
              (staff) => (
                <option
                  key={
                    staff.membershipId
                  }
                  value={
                    staff.membershipId
                  }
                >
                  {staff.displayName}
                </option>
              )
            )}
          </select>
        </label>

        <label>
          PIN
          <input
            type="password"
            inputMode="numeric"
            autoComplete="current-password"
            value={pin}
            onChange={(event) =>
              setPin(event.target.value)
            }
            minLength={4}
            maxLength={8}
          />
        </label>

        {error && (
          <p className="error-message">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={
            submitting ||
            !membershipId ||
            pin.length < 4
          }
        >
          {submitting
            ? "Signing in…"
            : "Sign in"}
        </button>
      </form>
    </BrandShell>
  );
}
